import { t } from 'i18next';
import { LS } from 'ls-ts';
import { FC, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

import {
  FormSubmissionResTypes,
  LoginRequest,
  LoginRequestValues,
  TFormData,
  TFormResponse,
  userService,
} from '#/client';
import { useAutoLogin, useSubmitAutoLogin } from '#/hooks/useAutoLogin';
import { FormInputVariants, StorageKeys } from '#/service/constants';
import { ApiError } from '#/service/error/errors';
import { Path } from '#/service/routes';
import { getDeviceInfo } from '#/service/user/userSessionManager';
import { noop, techSearchParams } from '#/service/utils';
import { authActions } from '#/store/auth';

import { createBasicFormConfig, TInputState } from '../Form';
import Form from '../Form/Form';
import FormInput from '../Form/FormInput';
import { Spinner } from '../Spinner';
import { ExtraOptions, RegistrationSection, SpinnerContainer } from './styled';
import { useLoginInputs } from './useLoginInputs';

interface Props {
  onSuccess: () => void;
  onCancel?: () => void;
  hideRememberMe?: boolean;
  hideRegister?: boolean;
  hideForgotPassword?: boolean;
  /** (Optional) Second Screen Auth Code that should be sent alongside the log in request */
  authCode?: string;
}

/** Login form for a standard (email + password) authentication flow */
export const LoginStandard: FC<Props> = ({
  onSuccess,
  onCancel,
  hideRememberMe = false,
  hideRegister = false,
  hideForgotPassword = false,
  authCode,
}) => {
  const { display, emailField, error, loading, rememberMe, setRememberMe } =
    useLoginInputs();
  const { isAutoLogin } = useAutoLogin();

  const submitForm = useCallback(
    async (data: TFormData): Promise<TFormResponse> => {
      const res = await performLogin(data, authCode);

      const { type } = res?._meta ?? {};

      if (type === FormSubmissionResTypes.SUCCESS) {
        if (emailField) {
          if (rememberMe.value) {
            const emailData = data[emailField.key] as string;
            LS.write(StorageKeys.LOGIN_EMAIL, emailData);
            emailField.defaultValue = emailData;
          } else {
            LS.remove(StorageKeys.LOGIN_EMAIL);
            emailField.defaultValue = undefined;
          }
        }

        onSuccess();
      }

      return res;
    },
    [authCode, emailField, onSuccess, rememberMe.value]
  );

  const registerText = t('web.login.registerText');
  const registerLink = t('web.login.registerLink');

  if (error) {
    // ACCEDOONECA-17556 - config flag is required to enable/disable auth.
    // if auth disabled, none of this should run
    // for now, comment out error so it doesn't interfere with apps not using auth
    // throw new Error(error);
  }

  const updateRememberMe = (_: string, state: TInputState) => {
    setRememberMe((prevState) => ({ ...prevState, ...state }));
  };

  useEffect(() => {
    setRememberMe({ value: !!emailField?.defaultValue });
  }, [emailField, setRememberMe]);

  const inputGroups = createBasicFormConfig('login', display ?? [], {
    submit: { textKeyPath: 'button.login', onClick: submitForm },
  });

  useSubmitAutoLogin(submitForm);

  return (
    <>
      {loading || isAutoLogin ? (
        <SpinnerContainer>
          <Spinner />
        </SpinnerContainer>
      ) : (
        <Form inputGroups={inputGroups} />
      )}
      {!isAutoLogin && (
        <>
          <ExtraOptions>
            {!hideRememberMe && !!emailField && (
              <FormInput
                dataKey="rememberMe"
                label={t('login.inputField.rememberMe.label')}
                state={rememberMe}
                updateErrorMessages={noop}
                updateState={updateRememberMe}
                variant={FormInputVariants.CHECKBOX}
              />
            )}
            {!hideForgotPassword && (
              <Link
                data-testid="forgotPasswordLink"
                onClick={onCancel}
                to={{
                  pathname: Path.ForgotPassword,
                  search: techSearchParams(location.search),
                }}
              >
                {t('login.forgotPasswordLink')}
              </Link>
            )}
          </ExtraOptions>
          {!hideRegister && (
            <RegistrationSection>
              {registerText}
              <Link
                aria-label={registerText + registerLink}
                onClick={onCancel}
                to={{
                  pathname: Path.Registration,
                  search: techSearchParams(location.search),
                }}
              >
                {registerLink}
              </Link>
            </RegistrationSection>
          )}
        </>
      )}
    </>
  );
};
LoginStandard.displayName = 'LoginStandard';

async function performLogin(values: TFormData, authCode?: string) {
  try {
    const req: LoginRequest = {
      deviceInfo: getDeviceInfo(),
      values: values as unknown as LoginRequestValues,
    };

    if (authCode) {
      req.secondScreen = {
        code: authCode,
      };
    }

    const resp = await userService.login(req);

    if (!authCode) {
      // When log in was executed with a second screen auth context then
      // we don't actually change anything regarding the current auth state
      await authActions.login(resp.data._meta.auth);
    }

    return resp.data;
  } catch (err) {
    if (err instanceof ApiError) {
      return err.details.content;
    }
  }
}
